/** Validação numérica em pt-BR sem depender de min/max nativos do browser. */

/**
 * Parse pt-BR / en numbers:
 * - "13.200" → 13200 (thousand sep)
 * - "1.234.567" → 1234567 (multiple thousand seps)
 * - "13,2" → 13.2 (decimal comma)
 * - "13.200,50" → 13200.5
 * - "13200.5" → 13200.5 (when only one dot and ≤2 fractional digits, treat as decimal)
 */
export function parseOptionalNumber(raw: string | number | null | undefined): number | null {
  if (raw === '' || raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;

  let v = String(raw).trim().replace(/\s/g, '');
  if (!v) return null;
  v = v.replace(/[^0-9.,-]/g, '');
  if (!v || v === '-' || v === '.' || v === ',') return NaN;

  const lastComma = v.lastIndexOf(',');
  const lastDot = v.lastIndexOf('.');
  const decimalIndex = Math.max(lastComma, lastDot);
  const dotCount = (v.match(/\./g) || []).length;

  if (decimalIndex === -1) {
    const n = Number(v.replace(/[^0-9-]/g, ''));
    return Number.isFinite(n) ? n : NaN;
  }

  // Multiple thousand dots, no decimal comma: 1.234.567 → 1234567
  if (lastComma === -1 && dotCount > 1) {
    const n = Number(v.replace(/[^0-9-]/g, ''));
    return Number.isFinite(n) ? n : NaN;
  }

  const decimalSep = v[decimalIndex];
  const frac = v.slice(decimalIndex + 1).replace(/[^0-9]/g, '');
  const intRaw = v.slice(0, decimalIndex).replace(/[^0-9-]/g, '');

  // If the "decimal" separator is '.' and fractional part has 3 digits with no
  // other separators, treat as thousand grouping (e.g. 13.200 → 13200).
  if (decimalSep === '.' && frac.length === 3 && lastComma === -1 && dotCount === 1) {
    const n = Number(`${intRaw}${frac}`);
    return Number.isFinite(n) ? n : NaN;
  }

  // Multiple thousand dots before decimal comma: 1.234.567,89
  if (decimalSep === ',' && dotCount >= 1) {
    const n = Number(`${intRaw}.${frac}`);
    return Number.isFinite(n) ? n : NaN;
  }

  const n = Number(`${intRaw}.${frac}`);
  return Number.isFinite(n) ? n : NaN;
}

export function assertNonNegative(
  raw: string | number | null | undefined,
  label: string
): string | null {
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseOptionalNumber(raw);
  if (n === null) return null;
  if (Number.isNaN(n)) return `${label}: informe um número válido.`;
  if (n < 0) return `${label}: o valor deve ser maior ou igual a 0.`;
  return null;
}

export function assertMinNumber(
  raw: string | number | null | undefined,
  min: number,
  label: string
): string | null {
  if (raw === '' || raw === null || raw === undefined) {
    return `${label}: preencha este campo.`;
  }
  const n = parseOptionalNumber(raw);
  if (n === null || Number.isNaN(n)) return `${label}: informe um número válido.`;
  if (n < min) return `${label}: o valor deve ser maior ou igual a ${min}.`;
  return null;
}
